// PostHog analytics wrapper (#133).
//
// ## Cookieless by design
// We run PostHog in `persistence: "memory"` mode — no cookies, no
// localStorage. That means **no cookie-consent banner is required** (the
// trade-off is session-scoped identity only: a returning user isn't
// re-linked across sessions). This is the posture #133 chose.
//
// ## Env-gated no-op
// When `REACT_APP_POSTHOG_KEY` is unset — local dev, CI, or a prod deploy
// that hasn't wired the key yet — every function here is a silent no-op,
// so the app behaves identically with or without analytics configured.
// Mirrors the Sentry init in `index.tsx` and the backend's
// no-op-when-unconfigured convention. Safe to ship before the PostHog
// project exists; flip it on by setting the env var.
//
// ## Deferred load (perf)
// posthog-js is one of the largest dependencies, so it is NOT in the main
// bundle: initAnalytics() schedules a dynamic import("posthog-js") for after
// first paint (window load + idle). Calls made before it resolves (identify
// on early sign-in, funnel events) are queued in order and replayed once
// PostHog is initialised, so nothing is lost. The public API is unchanged.
//
// ## What we deliberately DON'T do
// - No session recording (privacy; out of scope per #133).
// - No DOM autocapture (explicit funnel events only — less noise).
// - No PII in event properties; email/plan go via identify()/person
//   properties, never as custom event props.

import { whenIdle } from "./idle";

type PostHog = typeof import("posthog-js").default;

const KEY = process.env.REACT_APP_POSTHOG_KEY;
// PostHog cloud region is fixed when the project is created. Default to US
// cloud; override with REACT_APP_POSTHOG_HOST (e.g. https://eu.i.posthog.com)
// to match the project's region.
const HOST = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

// Bound the pre-load queue so a failed/blocked load can't grow it forever.
const MAX_QUEUE = 200;

let started = false;
let client: PostHog | null = null;
let queue: Array<(ph: PostHog) => void> = [];

function run(fn: (ph: PostHog) => void): void {
  if (!KEY) return;
  if (client) {
    fn(client);
    return;
  }
  if (queue.length < MAX_QUEUE) queue.push(fn);
}

async function load(): Promise<void> {
  try {
    const mod = await import("posthog-js");
    const posthog = mod.default;
    posthog.init(KEY as string, {
      api_host: HOST,
      persistence: "memory", // cookieless — no consent banner needed
      autocapture: false, // explicit events only
      capture_pageview: true, // initial pageview
      capture_pageleave: true,
      disable_session_recording: true, // replay off (privacy)
      // Supabase's email links land on /auth/callback#access_token=...&refresh_token=...
      // and the initial pageview captured that full URL, so PostHog was storing
      // live session tokens for every email signup. Scrub every event first.
      before_send: (event) => (event ? scrubAuthTokens(event) : event),
    });
    client = posthog;
    const pending = queue;
    queue = [];
    pending.forEach((fn) => fn(posthog));
  } catch {
    // Chunk blocked (ad blocker / offline): analytics stays a no-op.
    queue = [];
  }
}

// Query parameters that carry credentials in Supabase auth redirects.
const SECRET_PARAMS = ["access_token", "refresh_token", "provider_token", "provider_refresh_token", "token", "token_hash", "code"];

/**
 * Remove credentials from a URL-shaped string: drops the #fragment entirely
 * (Supabase puts session tokens there; nothing we analyse lives in it) and any
 * credential query parameters. Non-URL strings come back unchanged.
 */
export function scrubUrl(value: string): string {
  if (!/^https?:\/\//i.test(value) && !value.startsWith("/")) return value;
  let out = value;
  const hash = out.indexOf("#");
  if (hash >= 0) out = out.slice(0, hash);
  const q = out.indexOf("?");
  if (q >= 0) {
    const params = new URLSearchParams(out.slice(q + 1));
    let changed = false;
    SECRET_PARAMS.forEach((k) => {
      if (params.has(k)) {
        params.delete(k);
        changed = true;
      }
    });
    if (changed) {
      const rest = params.toString();
      out = out.slice(0, q) + (rest ? "?" + rest : "");
    }
  }
  return out;
}

function scrubProps(props: Record<string, unknown> | undefined): void {
  if (!props) return;
  Object.keys(props).forEach((k) => {
    const v = props[k];
    if (typeof v === "string") props[k] = scrubUrl(v);
  });
}

/**
 * PostHog before_send hook: strips auth tokens from every URL-like property on
 * the event, including the person properties PostHog sets alongside it
 * ($set / $set_once carry $current_url and $initial_current_url).
 */
export function scrubAuthTokens<T extends { properties?: Record<string, unknown>; $set?: Record<string, unknown>; $set_once?: Record<string, unknown> }>(event: T): T {
  scrubProps(event.properties);
  if (event.properties) {
    scrubProps(event.properties.$set as Record<string, unknown> | undefined);
    scrubProps(event.properties.$set_once as Record<string, unknown> | undefined);
  }
  scrubProps(event.$set);
  scrubProps(event.$set_once);
  return event;
}

/**
 * Start PostHog once, at app startup. No-op when unconfigured. The SDK is
 * fetched after first paint; calls made meanwhile are queued and replayed.
 */
export function initAnalytics(): void {
  if (!KEY || started) return;
  started = true;
  whenIdle(() => {
    void load();
  });
}

/**
 * Link the current (anonymous) session to a known user. Call on sign-in.
 * Email is the only PII we attach, and only here — never in event props.
 */
export function identifyUser(userId: string, props?: Record<string, unknown>): void {
  run((ph) => ph.identify(userId, props));
}

/** Update person-level properties (e.g. plan) without re-identifying. */
export function setUserProperties(props: Record<string, unknown>): void {
  run((ph) => {
    ph.setPersonProperties(props);
    // The founder's own admin account was ~77% of engagement events in the
    // 2026-10-31 usage review, which made every engagement metric read as
    // product traction. Stop capturing from an admin session once the plan is
    // known (the plan arrives after the first pageview, so that one still
    // lands). reset() below opts back in for the next person.
    if (props.plan === "admin") ph.opt_out_capturing();
  });
}

/** Clear identity on sign-out so the next user isn't conflated. */
export function resetAnalytics(): void {
  run((ph) => {
    ph.reset();
    ph.opt_in_capturing();
  });
}

/** Capture a funnel/product event. No PII in props. */
export function capture(event: string, props?: Record<string, unknown>): void {
  run((ph) => ph.capture(event, props));
}
